import { useState, type FormEvent } from 'react';
import type { ClientResponse } from '@clarity/contract';
import { useNavigate } from 'react-router-dom';
import { useClients, useCreateClient } from '../queries/index.js';
import { useClientContext } from '../lib/clientContext.js';
import { QueryState } from './QueryState.js';
import { Modal } from './Modal.js';
import { IconClients } from '../components/icons.js';

/** E.164: a leading + then 1–15 digits (mirrors the contract Client phone rule). */
const E164 = /^\+[1-9]\d{0,14}$/;

/**
 * Admin-only client management + the ENTRY POINT into a client's workspace and
 * its subscription.
 *
 * This is admin's home at the top level. Each client row offers two actions:
 * "Open workspace" drops the shell into that client's context (the dashboard IA),
 * and "Subscription" opens that client's billing page
 * (`/clients/:clientId/subscription`). Creating a client happens in a MODAL
 * (name + optional contact email/phone); it enables nothing (brand-model R2) —
 * platforms are derived from the brands later assigned (R3). The server enforces
 * admin-only access (Req 2.2).
 */
export function Clients() {
  const clients = useClients();
  const [creating, setCreating] = useState(false);

  const rows = clients.data?.status === 200 ? clients.data.body : [];

  return (
    <section>
      <div className="page-head">
        <div>
          <h2>Clients</h2>
          <p className="sub">
            Open a client&apos;s workspace, manage its subscription, or review its AI usage.
          </p>
        </div>
        <button type="button" onClick={() => setCreating(true)}>
          New client
        </button>
      </div>

      <QueryState isLoading={clients.isLoading} isError={clients.isError} label="clients">
        {rows.length === 0 ? (
          <div className="card empty">
            <IconClients className="empty__glyph" />
            <p>No clients yet. Create one with “New client” to get started.</p>
          </div>
        ) : (
          <ul className="list" aria-label="Clients">
            {rows.map((client) => (
              <ClientRow key={client.id} client={client} />
            ))}
          </ul>
        )}
      </QueryState>

      {creating && <NewClientModal onClose={() => setCreating(false)} />}
    </section>
  );
}

/** One client row: name + the two destinations (workspace, subscription). */
function ClientRow({ client }: { client: ClientResponse }) {
  const { enterClient } = useClientContext();
  const navigate = useNavigate();

  return (
    <li>
      <strong>{client.name}</strong>
      <span className="actions">
        <button
          type="button"
          className="sm ghost"
          onClick={() => navigate(`/clients/${client.id}/subscription`)}
        >
          Subscription
        </button>
        <button
          type="button"
          className="sm ghost"
          onClick={() => navigate(`/clients/${client.id}/advisor-usage`)}
        >
          AI usage
        </button>
        <button
          type="button"
          className="sm ghost"
          onClick={() => navigate(`/clients/${client.id}/users`)}
        >
          Users
        </button>
        <button type="button" className="sm" onClick={() => enterClient(client)}>
          Open workspace →
        </button>
      </span>
    </li>
  );
}

/**
 * The create-client modal (name + optional contact email/phone). A modal — not a
 * standing form card — because creating a client is an occasional, focused action
 * that should not sit permanently above the list. Validation mirrors the contract:
 * name required; a non-empty email must look like one; a non-empty phone must be
 * E.164. On success the list refetches and the modal closes.
 */
function NewClientModal({ onClose }: { onClose: () => void }) {
  const createClient = useCreateClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const emailTrim = email.trim();
  const phoneTrim = phone.trim();
  const emailInvalid = emailTrim !== '' && !/^\S+@\S+\.\S+$/.test(emailTrim);
  const phoneInvalid = phoneTrim !== '' && !E164.test(phoneTrim);
  const canSubmit = name.trim() !== '' && !emailInvalid && !phoneInvalid && !createClient.isPending;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    createClient.mutate(
      {
        body: {
          name: name.trim(),
          ...(emailTrim !== '' ? { email: emailTrim } : {}),
          ...(phoneTrim !== '' ? { phone: phoneTrim } : {}),
        },
      },
      {
        onSuccess: (res) => {
          if (res.status === 201) onClose();
        },
      },
    );
  }

  return (
    <Modal title="New client" onClose={onClose}>
      <form className="form-stack" onSubmit={onSubmit} aria-label="Create client">
        <label>
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Al Sahel Restaurants"
            required
          />
        </label>
        <label>
          Email <span className="muted">(optional)</span>
          <input
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="owner@client.example"
            aria-invalid={emailInvalid || undefined}
          />
        </label>
        <label>
          Phone <span className="muted">(optional, E.164)</span>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+962790000000"
            aria-invalid={phoneInvalid || undefined}
          />
        </label>

        {(emailInvalid || phoneInvalid) && (
          <p role="alert" className="error">
            {emailInvalid
              ? 'Enter a valid email address, or leave it blank.'
              : 'Phone must be E.164: a + then up to 15 digits (e.g. +962790000000).'}
          </p>
        )}

        <p className="muted">
          Platforms are enabled by importing brands for this client — not set here.
        </p>

        <div className="modal__actions">
          <button type="button" className="ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit}>
            {createClient.isPending ? 'Creating…' : 'Create client'}
          </button>
        </div>

        {createClient.isError && (
          <p role="alert" className="error">
            Could not create the client. Your entries are unchanged.
          </p>
        )}
      </form>
    </Modal>
  );
}
